import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { isAxiosError } from 'axios';
import { Bot, Loader2, PhoneCall, UserCheck, X } from 'lucide-react';

import { OperatorCallDialog } from '@/components/contacts/OperatorCallDialog';
import { TagBadge } from '@/components/contacts/TagBadge';
import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useDeleteCall } from '@/hooks/useCalls';
import {
  useAcknowledgeToolWatch,
  useContact,
  useContactTimeline,
  useDeleteContact,
} from '@/hooks/useContacts';
import {
  useConversation,
  useHandbackConversation,
  useMarkAsRead,
  useSendImage,
  useSendMessage,
  useSendTemplate,
  useTakeoverConversation,
} from '@/hooks/useMessaging';
import { useSetting } from '@/hooks/useSettings';
import { useTagDefinitions } from '@/hooks/useTagDefinitions';
import { cn } from '@/lib/utils';
import { MessageBubble } from '@/pages/messaging/components/MessageBubble';
import { MessageInput } from '@/pages/messaging/components/MessageInput';
import { useActiveContactsStore } from '@/stores/active-contacts.store';
import { useToolWatchStore } from '@/stores/tool-watch.store';

import { ActiveIndicator } from './ActiveIndicator';
import { ContactContextMenu } from './ContactContextMenu';
import { DeleteContactDialog } from './DeleteContactDialog';
import { EditContactDialog } from './EditContactDialog';
import { PhoneSessionBlock } from './PhoneSessionBlock';
import { ToolCallBubble } from './ToolCallBubble';
import { ToolInfoPopover } from './ToolInfoPopover';

import type { MessagingMessage } from '@/lib/api/messaging.api';
import type { TimelineItem, TimelineMessageItem } from '@repo/shared-types';

const toMessagingMessage = (item: TimelineMessageItem): MessagingMessage => ({
  id: item.id,
  conversation_id: item.conversation_id,
  direction: item.direction,
  status: item.status,
  type: item.type as MessagingMessage['type'],
  content: item.content,
  media_url: item.media_url,
  channel_message_id: null,
  sender_id: null,
  metadata: null,
  error_message: item.error_message ?? null,
  created_at: item.timestamp,
  updated_at: item.timestamp,
  reply_to_message_id: item.reply_to_message_id ?? null,
  reply_to_content: item.reply_to_content ?? null,
  reply_to_direction: item.reply_to_direction ?? null,
});

interface ChatCardProps {
  contactId: string;
  onClose: (contactId: string) => void;
  /** A contact from the list was dropped on this card — swap the chat. */
  onReplace?: (currentId: string, newId: string) => void;
}

export const ChatCard = ({ contactId, onClose, onReplace }: ChatCardProps) => {
  const { data: contact, isLoading: contactLoading, error: contactError } = useContact(contactId);

  // Self-heal: if the contact was deleted (404), drop this card from the
  // grid — its id lives in localStorage and would otherwise error forever.
  useEffect(() => {
    if (isAxiosError(contactError) && contactError.response?.status === 404) {
      onClose(contactId);
    }
  }, [contactError, contactId, onClose]);
  const { data: showToolCallsSetting } = useSetting('CONTACTS_SHOW_TOOL_CALLS');
  const showToolCalls = showToolCallsSetting?.value === 'true';
  const {
    data: timelineData,
    isLoading: timelineLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useContactTimeline(contactId, 30, showToolCalls);
  const flash = useToolWatchStore((s) => s.flashes[contactId]);
  const clearFlash = useToolWatchStore((s) => s.clearFlash);
  const acknowledgeToolWatch = useAcknowledgeToolWatch();
  const { byName: tagDefsByName } = useTagDefinitions();

  const conversations = contact?.messaging_conversations ?? [];
  const whatsappConv = conversations.find((c) => c.channel === 'WHATSAPP');
  const conversationId = whatsappConv?.id;

  const { data: conversation } = useConversation(conversationId);
  // An unclaimed contact keeps flashing until its chat is claimed — clicking
  // only stops the flash once claimed (or when there is nothing to claim).
  const flashStopsOnClick = !conversationId || !!conversation?.assigned_user_id;
  const sendMessage = useSendMessage();
  const sendTemplate = useSendTemplate();
  const sendImage = useSendImage();
  const markAsRead = useMarkAsRead();
  const takeover = useTakeoverConversation();
  const handback = useHandbackConversation();

  const isAiPaused = (conversation?.metadata as Record<string, unknown> | null)?.ai_paused === true;

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const [callChannel, setCallChannel] = useState<'pstn' | 'whatsapp' | undefined>(undefined);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteContactOpen, setDeleteContactOpen] = useState(false);
  const [deleteCallId, setDeleteCallId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const isLive = useActiveContactsStore((s) => s.activeIds.has(contactId));
  const deleteCallMutation = useDeleteCall();
  const deleteContactMutation = useDeleteContact();
  const { toast } = useToast();

  // Resolve configurationId for operator calls
  const configurationId = useMemo(() => {
    const fromConv = conversations.find((c) => c.configuration_id)?.configuration_id;
    if (fromConv) return fromConv;
    return undefined;
  }, [conversations]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const markedReadRef = useRef<string | null>(null);
  // Whether the view is glued to the newest message. Async content (call
  // transcripts) grows the thread after mount; while pinned we follow it.
  const pinnedRef = useRef(true);
  const prevScrollHeightRef = useRef(0);

  const items: TimelineItem[] = useMemo(() => {
    const flat = timelineData?.pages.flatMap((p) => p.items) ?? [];
    return [...flat].reverse();
  }, [timelineData]);

  useEffect(() => {
    isInitialLoadRef.current = true;
    markedReadRef.current = null;
    pinnedRef.current = true;
  }, [contactId]);

  // Keep the view glued to the last message while transcripts and images
  // load in and grow the content — until the user scrolls up themselves.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const prevItemCountRef = useRef(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isInitialLoadRef.current && items.length > 0) {
      el.scrollTop = el.scrollHeight;
      isInitialLoadRef.current = false;
      prevItemCountRef.current = items.length;
      return;
    }
    // Older pages prepend on "Load older history" — keep the viewport where
    // it was instead of jumping.
    if (isFetchingNextPage) {
      prevScrollHeightRef.current = el.scrollHeight;
    }
    // Auto-scroll to the latest message when new items arrive at the bottom.
    if (
      !isFetchingNextPage &&
      items.length > prevItemCountRef.current &&
      prevItemCountRef.current > 0 &&
      pinnedRef.current
    ) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevItemCountRef.current = items.length;
  }, [items.length, isFetchingNextPage]);

  useEffect(() => {
    if (!isFetchingNextPage && prevScrollHeightRef.current > 0 && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (
      conversationId &&
      conversation &&
      conversation.unread_count > 0 &&
      markedReadRef.current !== conversationId
    ) {
      markedReadRef.current = conversationId;
      markAsRead.mutate(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversation?.unread_count, markAsRead]);

  if (contactLoading) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border bg-white shadow-sm dark:bg-[#0b141a]">
        <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!contact) return null;

  const unreadCount = conversations.reduce((sum, c) => sum + (c.unread_count ?? 0), 0);

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border border-[#d1d7db] bg-white shadow-sm dark:border-[#2a3942] dark:bg-[#0b141a]',
        // Live-session glow (flash takes priority when both apply)
        isLive && !flash && 'ring-2 ring-[#00a884]/50',
        flash && 'animate-pulse ring-2 ring-amber-400/80',
        dragOver && 'ring-2 ring-[#00a884]',
      )}
      title={
        flash
          ? flashStopsOnClick
            ? `Watched tool "${flash.toolName}" was invoked`
            : `Watched tool "${flash.toolName}" was invoked — claim the chat to stop the flashing`
          : undefined
      }
      onDragOver={(e) => {
        if (!onReplace || !e.dataTransfer.types.includes('application/x-contact-id')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Ignore dragleave events fired when moving over child elements.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
      }}
      onDrop={(e) => {
        if (!onReplace) return;
        e.preventDefault();
        setDragOver(false);
        const droppedId = e.dataTransfer.getData('application/x-contact-id');
        if (droppedId) onReplace(contactId, droppedId);
      }}
      onClick={() => {
        if (flash && flashStopsOnClick) {
          clearFlash(contactId);
          acknowledgeToolWatch.mutate(contactId);
        }
      }}
      onContextMenu={(e) => {
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }
        e.preventDefault();
        setCtxMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      <div className="flex items-center gap-2 border-b border-[#d1d7db] bg-[#f0f2f5] px-2.5 py-1.5 dark:border-[#2a3942] dark:bg-[#202c33]">
        <div className="relative shrink-0">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#6b7b8d]">
            <span className="text-[11px] font-semibold text-white uppercase">
              {contact.name?.charAt(0) || '?'}
            </span>
          </div>
          <ActiveIndicator contactId={contactId} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate text-xs font-medium text-[#111b21] dark:text-[#e9edef]">
              {contact.name}
            </h3>
            {unreadCount > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#00a884] px-1 text-[9px] font-bold text-white">
                {unreadCount}
              </span>
            )}
            {isAiPaused && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
                HUMAN
              </span>
            )}
            {!isAiPaused && conversationId && (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
                AI
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {contact.phone && (
              <p className="shrink-0 truncate text-[9px] text-[#667781] dark:text-[#8696a0]">
                {contact.phone}
              </p>
            )}
            {(contact.tags ?? []).slice(0, 3).map((tag) => (
              <TagBadge key={tag} name={tag} definition={tagDefsByName.get(tag)} size="xs" />
            ))}
            {(contact.tags ?? []).length > 3 && (
              <span className="text-[9px] text-[#667781] dark:text-[#8696a0]">
                +{(contact.tags ?? []).length - 3}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          {showToolCalls && <ToolInfoPopover items={items} dark />}
          {contact.phone && configurationId && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-[#54656f] hover:bg-[#d9dbdf] hover:text-[#00a884] dark:text-[#aebac1] dark:hover:bg-[#2a3942]"
              title="Call contact"
              onClick={() => setCallOpen(true)}
            >
              <PhoneCall className="h-3.5 w-3.5" />
            </Button>
          )}
          {conversationId && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-[#54656f] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#aebac1] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]"
              title={isAiPaused ? 'Return to AI' : 'Take over'}
              onClick={() =>
                isAiPaused ? handback.mutate(conversationId) : takeover.mutate(conversationId)
              }
            >
              {isAiPaused ? <Bot className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-[#54656f] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#aebac1] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]"
            title="Remove from grid"
            onClick={() => onClose(contactId)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        }}
        className="flex-1 overflow-y-auto bg-[#efeae2] px-3 py-2 dark:bg-[#0b141a]"
        style={{
          // Neutral gray dots read as a subtle doodle on both themes.
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='p' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='20' cy='20' r='1' fill='%2380808014'/%3E%3C/pattern%3E%3C/defs%3E%3Crect fill='url(%23p)' width='200' height='200'/%3E%3C/svg%3E\")",
        }}
      >
        <div ref={contentRef} className="space-y-1">
          {(hasNextPage || isFetchingNextPage) && (
            <div className="flex justify-center pb-1">
              {isFetchingNextPage ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#667781] dark:text-[#8696a0]" />
              ) : (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  className="cursor-pointer text-[10px] text-[#667781] transition-colors duration-150 hover:text-[#111b21] dark:text-[#8696a0] dark:hover:text-[#e9edef]"
                >
                  Load older history
                </button>
              )}
            </div>
          )}
          {timelineLoading ? (
            <div className="flex min-h-[60px] items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-4 text-center text-xs text-[#667781] dark:text-[#8696a0]">
              No messages yet
            </p>
          ) : (
            items.map((item) =>
              item.kind === 'tool' ? (
                <ToolCallBubble key={item.id} item={item} dark />
              ) : item.kind === 'call' ? (
                <PhoneSessionBlock
                  key={item.id}
                  item={item}
                  onDelete={setDeleteCallId}
                  compact
                  dark
                />
              ) : (
                <MessageBubble key={item.id} message={toMessagingMessage(item)} compact />
              ),
            )
          )}
          <div ref={endRef} />
        </div>
      </div>

      {conversationId && (
        <div className="border-t border-[#d1d7db] bg-[#f0f2f5] dark:border-[#2a3942] dark:bg-[#202c33]">
          <MessageInput
            onSend={(content) => sendMessage.mutate({ conversationId, content })}
            onSendTemplate={
              whatsappConv?.channel === 'WHATSAPP'
                ? (templateName, language, components, renderedText) =>
                    sendTemplate.mutate({
                      conversationId,
                      templateName,
                      language,
                      components,
                      renderedText,
                    })
                : undefined
            }
            onSendImage={
              whatsappConv?.channel === 'WHATSAPP'
                ? (file) => sendImage.mutate({ conversationId, file })
                : undefined
            }
            disabled={sendMessage.isPending}
            channel={whatsappConv?.channel}
          />
        </div>
      )}

      {ctxMenu && (
        <ContactContextMenu
          contactId={contactId}
          position={ctxMenu}
          onClose={() => setCtxMenu(null)}
          conversationId={conversationId}
          onCall={(ch) => {
            setCallChannel(ch);
            setCallOpen(true);
          }}
          onEdit={() => setEditOpen(true)}
          onDeleteContact={() => setDeleteContactOpen(true)}
        />
      )}

      <EditContactDialog contact={contact} open={editOpen} onOpenChange={setEditOpen} />
      <DeleteContactDialog
        contact={contact}
        open={deleteContactOpen}
        isDeleting={deleteContactMutation.isPending}
        onOpenChange={setDeleteContactOpen}
        onConfirm={() => {
          deleteContactMutation.mutate(contactId, {
            onSuccess: () => {
              setDeleteContactOpen(false);
              toast({
                variant: 'success',
                title: 'Contact deleted',
                description: 'The contact and all their calls and chats were deleted.',
              });
              onClose(contactId);
            },
            onError: () => {
              toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Failed to delete the contact. Please try again.',
              });
            },
          });
        }}
      />
      <DeleteConfirmationDialog
        open={Boolean(deleteCallId)}
        resourceName="call"
        resourceIdentifier={deleteCallId ?? undefined}
        onCancel={() => setDeleteCallId(null)}
        onConfirm={() => {
          if (!deleteCallId) return;
          deleteCallMutation.mutate(deleteCallId, {
            onSuccess: () => {
              setDeleteCallId(null);
              toast({ variant: 'success', title: 'Call deleted' });
            },
            onError: () => {
              toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Failed to delete call.',
              });
            },
          });
        }}
      />

      {contact.phone && configurationId && (
        <OperatorCallDialog
          open={callOpen}
          onOpenChange={(v) => {
            setCallOpen(v);
            if (!v) setCallChannel(undefined);
          }}
          contactId={contactId}
          contactName={contact.name ?? ''}
          contactPhone={contact.phone}
          configurationId={configurationId}
          defaultChannel={callChannel}
        />
      )}
    </div>
  );
};
